import { PlusOutlined } from "@ant-design/icons"
import { Button } from "antd"
import React from "react"

import { ActionTooltip } from "../ActionTooltip"

interface TableAddActionProps {
  /** Names the action for this table, e.g. "Add Milestone". Tooltip + accessible name. */
  label: string
  allowed?: boolean
  disabled?: boolean
  onAdd: () => void
  children: React.ReactNode
}

/**
 * Anchors the floating "+" to the bottom-right of a table, staying visible
 * without ever covering the last row or the paginator.
 *
 * `position: sticky`, not `absolute` or `fixed`. Both of those were tried
 * and both failed: `absolute` ties the button to the table's own content
 * height, so it rides up on a near-empty result and needs page scroll to
 * reach on a tall one; `fixed` fixes visibility but pins the button to a
 * spot on the viewport that can land on top of the paginator or the last
 * row, which is the exact overlap `TableAddAction` exists to prevent.
 * `sticky` keeps the button pinned near the viewport's bottom edge while
 * its normal-flow position is scrolling through view, instead of scrolling
 * off with it. No table height has to be measured for this to work.
 *
 * Unlike `absolute`, `sticky` occupies real space in normal flow — no
 * separate reserved strip is needed below the table, or the button's own
 * box and that strip would double up, stacking two gaps instead of one.
 */
export function TableAddAction({
  label,
  allowed = true,
  disabled = false,
  onAdd,
  children
}: TableAddActionProps) {
  if (!allowed) return <>{children}</>

  return (
    <>
      {children}
      {/* Keep the action outside the support launcher's expanded footprint.
          The launcher is 56px wide at the right edge; pr-20 leaves another
          24px between the two controls when both are visible. */}
      <div className="sticky bottom-3 z-10 mt-3 flex justify-end pr-20">
        <ActionTooltip title={label} placement="left">
          <Button
            type="primary"
            shape="circle"
            aria-label={label}
            icon={<PlusOutlined />}
            disabled={disabled}
            onClick={onAdd}
            className="size-11 shadow-lg md:size-10"
          />
        </ActionTooltip>
      </div>
    </>
  )
}

export default TableAddAction
